import { FC, useEffect, useState } from 'react';
import {
  ComponentRendering,
  Field,
  LayoutServiceData,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { GetStaticPropsContext } from 'next';

import { useSiteSettings, withTheme } from '@gn-audio/jss-components';
import { Accessory, getFamilyAccessories, getLocaleWithRegion } from '@gn-audio/jss-core';

import { getSfccProductPrices } from 'api/pricesApi/getSfccProductPrices';
import { StandardPriceModel } from 'api/pricesApi/pricesModel';
import { parseSCLookupValue } from 'common/siteCoreHelpers';
import SliderWithPrice from 'components/Product/AccessorySpot/SliderWithPrice';
import { AccessorySpotProps } from 'composables/Product/AccessorySpot/AccessorySpotTypes';

const AccessorySpot: FC<AccessorySpotProps> = ({ accessories, fields }) => {
  const [prices, setPrices] = useState<StandardPriceModel[]>([]);
  const { ecom } = useSiteSettings();

  const headline = fields.headline;

  useEffect(() => {
    const fetchPrices = async () => {
      if (ecom?.ecomEnabled) {
        const prices = await getSfccProductPrices(accessories.map((accessory) => accessory.sku));
        setPrices(prices);
      } else {
        setPrices(new Array(accessories.length).fill(null));
      }
    };
    void fetchPrices();
  }, [accessories, ecom?.ecomEnabled]);

  if (accessories.length === 0) {
    return null;
  }

  const accessoriesWithPrices = accessories.map((accessory, index) => ({
    ...accessory,
    price: prices[index],
  }));

  return (
    <div className="bg-theme container-fluid overflow-hidden">
      <div className="container">
        <SliderWithPrice header={headline} cards={accessoriesWithPrices} />
      </div>
    </div>
  );
};

const mapAccessoriesWithSku = (accessories: Accessory[]): Accessory[] => {
  return accessories.map((accessory) => ({
    ...accessory,
    pageUrl: `${accessory.pageUrl}?sku=${accessory.sku}`,
  }));
};

export const getStaticProps = async (
  rendering: ComponentRendering<AccessorySpotProps>,
  { sitecore }: LayoutServiceData,
  context: GetStaticPropsContext
) => {
  try {
    const product = (sitecore.route?.fields?.Product as Field<string>)?.value;

    if (!product) {
      return { rendering, accessories: [] };
    }
    const familyLookupCode = parseSCLookupValue(product);
    const language = getLocaleWithRegion(context.locale);
    const accessories = await getFamilyAccessories(+familyLookupCode.id, language);
    return { rendering, accessories: accessories ? mapAccessoriesWithSku(accessories) : [] };
  } catch (error) {
    console.log(error);
    const { params = {} } = rendering;
    return { config: {}, params };
  }
};

export default withTheme({
  defaultTheme: {
    backgroundColor: 'white',
    foregroundColor: 'black',
    darkTheme: false,
  },
})(withDatasourceCheck()<AccessorySpotProps>(AccessorySpot));
